import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero } from "../../components/site/Page";
import { Portrait } from "../../components/site/Portrait";
import { staffPortraitsQuery, useStaffPortraits } from "@/lib/staff-portraits";
import { staffProfilesQuery, type StaffProfileMap } from "@/lib/staff-profiles";
import maria from "../../assets/staff/portraits/maria.webp";
import beatriz from "../../assets/staff/portraits/beatriz.webp";
import bojan from "../../assets/staff/portraits/bojan.webp";
import anna from "../../assets/staff/portraits/anna.webp";
import blanca from "../../assets/staff/portraits/blanca.webp";
import malin from "../../assets/staff/portraits/malin.webp";
import svetlana from "../../assets/staff/portraits/svetlana.webp";
import farhad from "../../assets/staff/portraits/farhad.webp";
import bojan2 from "../../assets/staff/portraits/bojan2.webp";
import erik from "../../assets/staff/portraits/erik.webp";
import { fillYearsFromProfiles } from "@/content/staff-facts";

const title = "Nuestro equipo y profesorado | Swedish International School";
const description =
  "Conoce al equipo de Swedish International School en Estepona – Marbella: dirección, docentes, educadoras de preescolar y tutores con titulación sueca.";

export const Route = createFileRoute("/es/equipo")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/es/equipo") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "es_ES" },
      ...ogImage(),
    ],
    links: seoLinks("/es/equipo"),
  }),
  loader: async ({ context }) => {
    const [, profileData] = await Promise.all([
      context.queryClient.ensureQueryData(staffPortraitsQuery),
      context.queryClient.ensureQueryData(staffProfilesQuery),
    ]);
    return { profiles: profileData.profiles as StaffProfileMap };
  },
  component: Equipo,
});

const staff = [
  {
    name: "Maria",
    role: "Directora",
    key: "maria",
    image: maria,
    paragraphs: [
      "Soy maestra titulada y especialista en pedagogía terapéutica, y he pasado los últimos {years:maria} años trabajando como directora en distintos colegios de Helsingborg. Mi mejor herramienta es construir buenas relaciones profesionales con el alumnado, el equipo y las familias, algo que crea un clima positivo donde alcanzar objetivos y cooperar resultan naturales. Lo hago con entusiasmo, con la relación, la motivación y la comunicación como columna vertebral de mi liderazgo.",
      "Me despejo la cabeza corriendo o, sencillamente, pasando tiempo en nuestro velero.",
    ],
  },
  {
    name: "Beatriz",
    role: "Directora técnica",
    key: "beatriz",
    image: beatriz,
    paragraphs: [
      "Beatriz lleva {years:beatriz} años trabajando como maestra de preescolar y primaria, y en Swedish International School desde la primavera de 2025. Tiene un interés genuino por la lectura, el deporte y la naturaleza, y además es profesora de yoga titulada.",
      "«En mi papel como docente intento dar a mi alumnado las herramientas para desarrollarse tanto académica como socialmente. Me gusta citar a Alexander K. Trenfor para definir cómo entiendo mi labor: “Los mejores maestros son los que te dicen dónde mirar, pero no te dicen qué ver”.»",
    ],
  },
  {
    name: "Bojan",
    role: "Profesor de educación física y educador de extraescolares",
    key: "bojan",
    image: bojan,
    paragraphs: [
      "Bojan lleva {years:bojan} años trabajando como profesor de educación física y educador de extraescolares, y en Swedish International School desde el otoño de 2023. Con experiencia como profesor de educación física, educador, masajista, asesor nutricional y responsable de mantenimiento, también ha jugado al baloncesto a nivel de élite en Suecia y en el extranjero.",
      "«Para mí es importante crear un entorno que favorezca la salud física y mental del alumnado. Como docente, busco construir relaciones de confianza y animar a cada alumno a llegar lo más lejos posible en su desarrollo académico.»",
      "Bojan también tutoriza educación física y salud en los cursos 7–9 y bachillerato, y es responsable de los entrenamientos junto con el Malmö FF.",
    ],
  },
  {
    name: "Anna",
    role: "Responsable de preescolar",
    key: "anna",
    image: anna,
    paragraphs: [
      "Anna tiene {years:anna} años de experiencia trabajando con niños en preescolar y en el colegio.",
      "«Fundé un preescolar en Orsa (Suecia) y desde entonces he sido responsable de su actividad pedagógica y de su desarrollo. Tener mi propia empresa me ha enseñado que cada niño es único, con sus propias capacidades y fortalezas. Mi papel como educadora es descubrir esas cualidades y ayudar a desarrollarlas. Mi mayor fortaleza es ganarme la confianza de los niños, y con ella construimos juntos una relación basada en el respeto.»",
    ],
  },
  {
    name: "Blanca",
    role: "Clase preescolar y preescolar",
    key: "blanca",
    image: blanca,
    paragraphs: [
      "Blanca tiene más de {years:blanca} años de experiencia enseñando principalmente a alumnado de secundaria y bachillerato, y también con niños de preescolar y primaria.",
      "«Actualmente trabajo en el preescolar de Swedish International School, enseñando español a los más pequeños. Lo más importante para mí es que mis alumnos comprendan y aprendan las materias que imparto, para que en el futuro puedan continuar sus estudios con una base sólida, tanto en conocimientos como en técnica de estudio.»",
    ],
  },
  {
    name: "Malin",
    role: "Maestra de primeros cursos, F–3",
    key: "malin",
    image: malin,
    paragraphs: [
      "Malin lleva en total {years:malin} años trabajando en la escuela y desde el otoño de 2024 enseña en Swedish International School como maestra de los cursos F–3. También es responsable de tecnología, plástica y GOTD en los cursos 4–6.",
      "«Para mí la escuela es un lugar donde ocurre la magia. Mi objetivo es crear un entorno seguro e inspirador donde el alumnado se atreva a crecer y alcanzar todo su potencial. Poder acompañar el camino de cada alumno es un privilegio, y me apasiona marcar la diferencia cada día.»",
    ],
  },
  {
    name: "Svetlana",
    role: "Tutora de los cursos 4–6",
    key: "svetlana",
    image: svetlana,
    paragraphs: [
      "Svetlana lleva {years:svetlana} años trabajando como maestra de primaria y secundaria, y en Swedish International School desde el otoño de 2023.",
      "«Para mí es importante crear un entorno de aprendizaje seguro, accesible y creativo para todo mi alumnado, acompañar su desarrollo y crecer con ellos. Me apasionan los idiomas y las metodologías que desarrollan el lenguaje. El español siempre ha sido una parte importante de mi vida, así que trabajar aquí se siente como volver a casa.»",
      "Svetlana también imparte español en los cursos 1–3 y tutoriza español para el alumnado de 7–9 y bachillerato.",
    ],
  },
  {
    name: "Farhad",
    role: "Tutor de los cursos 7–9 y bachillerato, entrenador de la academia MFF",
    key: "farhad",
    image: farhad,
    paragraphs: [
      "Profesor titulado de matemáticas y ciencias, tanto para primaria como para secundaria.",
      "«A lo largo de mis más de {years:farhad} años en este tipo de colegios he comprobado que el factor común en el desarrollo académico del alumnado es la confianza del propio alumno en su capacidad de afrontar distintas tareas. Por eso trabajo mucho precisamente eso. Y para ello es imprescindible que el alumno se sienta seguro en su entorno escolar.»",
    ],
  },
  {
    name: "Bojan",
    role: "Profesor de música",
    key: "bojan2",
    image: bojan2,
    paragraphs: [
      "Bojan es un profesor de música dedicado y apasionado, con un amor profundo por la música desde siempre. El interés tomó forma con solo nueve años, cuando tocar el piano se convirtió en parte natural del día a día.",
      "Su formación como profesor de música, con especialidad en piano de jazz, se combina con una amplia competencia como multiinstrumentista de piano, guitarra y batería. Entiende la enseñanza musical como una parte central del desarrollo cognitivo, creativo y psicomotor de los niños, y sus clases se imparten en inglés y español.",
    ],
  },
  {
    name: "Erik",
    role: "Tutor de alumnado a distancia y profesor de matemáticas y ciencias, cursos 4–6",
    key: "erik",
    image: erik,
    paragraphs: [
      "Trabajo en la enseñanza desde 2017 y tengo experiencia en colegios internacionales y privados de Alemania y España. También soy biólogo titulado, especializado en comportamiento y cuidado animal, y profesor de biología de secundaria.",
      "Mi fortaleza docente es crear un entorno de aprendizaje inclusivo donde todo el alumnado se sienta partícipe y visto. Doy mucha importancia a motivar y animar a mis alumnos a pensar de forma creativa y a desarrollar su curiosidad por aprender.",
    ],
  },
];

function Equipo() {
  const { profiles } = Route.useLoaderData();
  const portraits = useStaffPortraits();
  const visible = staff.filter((p) => !portraits[p.key]?.hidden);
  return (
    <>
      <PageHero
        eyebrow="Nuestro equipo"
        title="Nuestro personal"
        intro="Nuestra meta es que toda la enseñanza en sueco la impartan docentes con titulación docente sueca. El español lo imparten docentes con la titulación correspondiente. Conoce al equipo."
      />

      <div className="mx-auto max-w-5xl space-y-8 px-5 py-16">
        {visible.map((p, i) => (
          <article
            key={`${p.name}-${i}`}
            className="grid gap-6 rounded-xl border border-border bg-card p-6 shadow-soft sm:grid-cols-[10rem_1fr] sm:p-8"
          >
            <Portrait
              src={p.image}
              personKey={p.key}
              priority={i < 2}
              alt={`Retrato de ${p.name}, ${p.role.toLowerCase()}`}
              className="h-40 w-40"
            />
            <div>
              <h2 className="text-xl font-semibold">{p.name}</h2>
              <p className="mt-1 text-sm text-primary">{p.role}</p>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/90">
                {p.paragraphs.map((text, j) => (
                  <p key={j}>{fillYearsFromProfiles(text, profiles)}</p>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>

      <FaqBlock items={faqFor("personal", "es")} locale="es" />

      <CtaBand locale="es" />
      <div className="h-20" />
    </>
  );
}
